import * as THREE from 'three';
import type { Airframe } from '../config/airframes';
import type { Checksum, Checksummable } from '../core/Checksum';

/**
 * Aircraft state (SPEC §3.2.1) and the frame conventions everything else depends on.
 *
 * **Body frame** matches Three.js camera convention so the nose camera at M2 is the aircraft's
 * orientation with no extra basis change: **−Z forward, +Y up, +X right**.
 *
 * **Angular velocity** is stored in the standard aerospace sense as (p, q, r) in `angularVelocity`
 * .x/.y/.z — roll rate positive right-wing-down, pitch rate positive nose-up, yaw rate positive
 * nose-right. That is *not* the same as a rotation vector in the Three.js basis; the conversion
 * lives in `bodyRotationVector` and nowhere else. Mixing the two is the most expensive mistake
 * available in this file.
 *
 * Units: metres, m/s, radians, rad/s, kg, pascals.
 */

/** Body-frame basis vectors, Three.js convention. Read-only — never write through these. */
export const BODY_FORWARD = Object.freeze(new THREE.Vector3(0, 0, -1));
export const BODY_UP = Object.freeze(new THREE.Vector3(0, 1, 0));
export const BODY_RIGHT = Object.freeze(new THREE.Vector3(1, 0, 0));

/** Commanded controls for one tick. Produced by the input layer or an autopilot. */
export interface ControlInput {
  /** −1..1, positive rolls right (right wing down). */
  rollCmd: number;
  /** −1..1, positive pitches the nose up. */
  pitchCmd: number;
  /** −1..1, positive yaws the nose right. */
  yawCmd: number;
  /** 0..1 commanded throttle. Actual thrust lags this (SPEC §3.2.2). */
  throttle: number;
}

/** A neutral control input. */
export function createControlInput(): ControlInput {
  return { rollCmd: 0, pitchCmd: 0, yawCmd: 0, throttle: 0 };
}

export interface SpawnState {
  positionM: THREE.Vector3;
  /** Heading in radians, clockwise from north (−Z). */
  headingRad: number;
  airspeedMs: number;
  throttle: number;
  /**
   * Pitch attitude at spawn, radians. The velocity is laid down horizontally, so this is also
   * the initial angle of attack — set it to the trim α or the aircraft starts out of trim and
   * sinks (see computeTrim).
   */
  pitchRad?: number;
}

// Module-scope scratch: these accessors are read every frame by the HUD and readout.
const scratchForward = new THREE.Vector3();
const scratchUp = new THREE.Vector3();
const scratchRight = new THREE.Vector3();
const spawnPitchScratch = new THREE.Quaternion();

export class FlightState implements Checksummable {
  // --- Integrated state
  readonly position = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();
  readonly orientation = new THREE.Quaternion();
  /** (p, q, r) body rates, rad/s. See the class comment before touching this. */
  readonly angularVelocity = new THREE.Vector3();

  massKg = 0;
  fuelKg = 0;
  /** 1 = undamaged, 0 = destroyed. */
  integrity = 1;

  /**
   * The angle of attack the airframe is trimmed for, radians. Static pitch stability restores
   * toward this, so it is a trim wheel rather than a constant: set at spawn, adjustable later.
   */
  trimAlphaRad = 0;

  /** Commanded throttle, 0..1. */
  throttleCommand = 0;
  /** Throttle after the first-order engine lag, 0..1. */
  throttleActual = 0;

  // --- Previous tick, for render interpolation (SPEC §4.3)
  readonly previousPosition = new THREE.Vector3();
  readonly previousOrientation = new THREE.Quaternion();

  // --- Derived each tick, never integrated
  /** True airspeed relative to the air mass, m/s. */
  airspeedMs = 0;
  alphaRad = 0;
  betaRad = 0;
  dynamicPressurePa = 0;
  /** n = L/(m·g). */
  loadFactor = 1;
  airDensityKgM3 = 0;
  /** Aerodynamic lift magnitude this tick, newtons. */
  liftN = 0;
  dragN = 0;
  thrustN = 0;

  // --- Discrete conditions
  engineRunning = true;
  structuralFailure = false;
  /** True while |α| is past the stall angle. */
  stalled = false;
  /** 0 below the stall, rising to 1 across the post-stall band. */
  stallDepth = 0;

  /** Altitude above the reference plane, metres. Replaced by terrain AGL at M3. */
  get altitudeM(): number {
    return this.position.y;
  }

  /** Body forward axis in world space. Writes into a shared scratch — copy if you keep it. */
  forward(): THREE.Vector3 {
    return scratchForward.copy(BODY_FORWARD).applyQuaternion(this.orientation);
  }

  /** Body up axis in world space. Shared scratch. */
  up(): THREE.Vector3 {
    return scratchUp.copy(BODY_UP).applyQuaternion(this.orientation);
  }

  /** Body right axis in world space. Shared scratch. */
  right(): THREE.Vector3 {
    return scratchRight.copy(BODY_RIGHT).applyQuaternion(this.orientation);
  }

  /** Pitch attitude, radians, positive nose-up. */
  get pitchRad(): number {
    return Math.asin(clampUnit(this.forward().y));
  }

  /** Heading, radians clockwise from north (−Z), in [0, 2π). */
  get headingRad(): number {
    const forward = this.forward();
    const heading = Math.atan2(forward.x, -forward.z);
    return heading < 0 ? heading + Math.PI * 2 : heading;
  }

  /** Bank angle, radians, positive right-wing-down. */
  get bankRad(): number {
    const rightY = this.right().y;
    const upY = this.up().y;
    return Math.atan2(-rightY, upY);
  }

  /** Roll rate p, rad/s, positive right-wing-down. */
  get rollRateRadS(): number {
    return this.angularVelocity.x;
  }

  /** Pitch rate q, rad/s, positive nose-up. */
  get pitchRateRadS(): number {
    return this.angularVelocity.y;
  }

  /** Yaw rate r, rad/s, positive nose-right. */
  get yawRateRadS(): number {
    return this.angularVelocity.z;
  }

  /** Vertical speed, m/s, positive climbing. */
  get verticalSpeedMs(): number {
    return this.velocity.y;
  }

  /** Groundspeed, m/s. */
  get groundSpeedMs(): number {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  /**
   * Convert the (p, q, r) body rates into a rotation vector in the Three.js body basis.
   * Roll is about −Z, pitch about +X, yaw about −Y. Writes into `out`.
   */
  bodyRotationVector(out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.angularVelocity.y, -this.angularVelocity.z, -this.angularVelocity.x);
  }

  /** Specific energy, J/kg: v²/2 + g·h. The currency of air combat manoeuvring. */
  specificEnergyJKg(gravityMs2: number): number {
    return 0.5 * this.velocity.lengthSq() + gravityMs2 * this.position.y;
  }

  /** Place the aircraft at a spawn condition, wings level, at the given heading and speed. */
  spawn(airframe: Airframe, spawn: SpawnState): void {
    this.position.copy(spawn.positionM);
    this.previousPosition.copy(spawn.positionM);

    // Heading first, then pitch in the body frame. Velocity is laid down along the *horizontal*
    // heading, so the pitch attitude is exactly the initial angle of attack.
    this.orientation.setFromAxisAngle(BODY_UP, -spawn.headingRad);
    this.velocity
      .copy(BODY_FORWARD)
      .applyQuaternion(this.orientation)
      .multiplyScalar(spawn.airspeedMs);

    const pitchRad = spawn.pitchRad ?? 0;
    // Velocity is horizontal, so the spawn attitude *is* the spawn α — and therefore the trim.
    this.trimAlphaRad = pitchRad;
    if (pitchRad !== 0) {
      spawnPitchScratch.setFromAxisAngle(BODY_RIGHT, pitchRad);
      this.orientation.multiply(spawnPitchScratch);
    }
    this.previousOrientation.copy(this.orientation);
    this.angularVelocity.set(0, 0, 0);

    this.massKg = airframe.massKg;
    this.fuelKg = airframe.fuelKg;
    this.integrity = 1;
    this.throttleCommand = spawn.throttle;
    this.throttleActual = spawn.throttle;

    this.airspeedMs = spawn.airspeedMs;
    this.alphaRad = 0;
    this.betaRad = 0;
    this.dynamicPressurePa = 0;
    this.loadFactor = 1;
    this.airDensityKgM3 = 0;
    this.liftN = 0;
    this.dragN = 0;
    this.thrustN = 0;

    this.engineRunning = true;
    this.structuralFailure = false;
    this.stalled = false;
    this.stallDepth = 0;
  }

  /** Copy current transform into the previous-tick slots. Called at the top of each tick. */
  storePreviousTransform(): void {
    this.previousPosition.copy(this.position);
    this.previousOrientation.copy(this.orientation);
  }

  /** Everything integrated, so a divergence anywhere shows up in the checksum. */
  checksum(into: Checksum): void {
    into.addFloat(this.position.x);
    into.addFloat(this.position.y);
    into.addFloat(this.position.z);
    into.addFloat(this.velocity.x);
    into.addFloat(this.velocity.y);
    into.addFloat(this.velocity.z);
    into.addFloat(this.orientation.x);
    into.addFloat(this.orientation.y);
    into.addFloat(this.orientation.z);
    into.addFloat(this.orientation.w);
    into.addFloat(this.angularVelocity.x);
    into.addFloat(this.angularVelocity.y);
    into.addFloat(this.angularVelocity.z);
    into.addFloat(this.throttleActual);
    into.addFloat(this.fuelKg);
    into.addFloat(this.integrity);
  }
}

/** Clamp to [-1, 1] before asin/acos, where a float overshoot would produce NaN. */
export function clampUnit(value: number): number {
  return value < -1 ? -1 : value > 1 ? 1 : value;
}
